// Marketplace API client + shared types
import { authHeaders } from './marketplace-auth';

export interface MarketplaceListing {
  id: number;
  user_id?: number;
  category: string;
  subcategory?: string | null;
  title: string;
  description?: string;
  price: number | string;
  price_type: string;
  currency?: string;
  condition: string;
  brand?: string | null;
  model?: string | null;
  year?: number | null;
  length_m?: number | string | null;
  engine_hours?: number | null;
  region: string;
  city?: string | null;
  images: string[];
  status?: string;
  views?: number;
  featured?: boolean;
  slug: string;
  created_at: string;
  updated_at?: string;
  seller_name?: string;
  seller_region?: string;
  inquiries?: number;
}

export interface MarketplaceListResponse {
  success: boolean;
  listings: MarketplaceListing[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const BASE = '/.netlify/functions';

export interface MarketplaceFilters {
  category?: string;
  subcategory?: string;
  region?: string;
  condition?: string;
  min_price?: string;
  max_price?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export async function fetchMarketplaceListings(filters: MarketplaceFilters = {}): Promise<MarketplaceListResponse> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const res = await fetch(`${BASE}/marketplace-list?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to load listings');
  return res.json();
}

export async function fetchMarketplaceListing(slugOrId: string): Promise<{ listing: MarketplaceListing; related: MarketplaceListing[] }> {
  const param = /^\d+$/.test(slugOrId) ? `id=${slugOrId}` : `slug=${encodeURIComponent(slugOrId)}`;
  const res = await fetch(`${BASE}/marketplace-get?${param}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Listing not found');
  return data;
}

export async function fetchMyListings(): Promise<MarketplaceListing[]> {
  const res = await fetch(`${BASE}/marketplace-mine`, { headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load your listings');
  return data.listings;
}

export interface CreateListingPayload {
  category: string;
  subcategory?: string;
  title: string;
  description: string;
  price?: number;
  price_type: string;
  condition: string;
  brand?: string;
  model?: string;
  year?: number;
  length_m?: number;
  engine_hours?: number;
  region: string;
  city?: string;
  images: string[];
}

export async function createMarketplaceListing(payload: CreateListingPayload): Promise<MarketplaceListing> {
  const res = await fetch(`${BASE}/marketplace-create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create listing');
  return data.listing;
}

export async function deleteMarketplaceListing(listingId: number): Promise<void> {
  const res = await fetch(`${BASE}/marketplace-delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ listing_id: listingId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete listing');
}

export async function sendInquiry(payload: {
  listing_id: number;
  buyer_name: string;
  buyer_email: string;
  buyer_phone?: string;
  message: string;
}): Promise<string> {
  const res = await fetch(`${BASE}/marketplace-inquiry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to send inquiry');
  return data.message;
}
